/**
 * Code splitting (first paint stays fast): Pixi, pixi-filters and the whole stadium renderer live in their own
 * chunk, loaded the first time a match (or a replay reel) mounts a renderer — or earlier, when the browser is idle
 * (`preloadRenderer`). `lazyRendererFactory` returns a `CreateMatchRenderer` that keeps the synchronous factory
 * contract: the returned renderer forwards to the real one once its chunk is in, and `mount()` awaits the chunk.
 */
import type {
  CreateMatchRenderer,
  MatchRenderer,
  RenderOptions,
  RendererContext,
  ReplayLook,
} from '@hf/render-core/types';

export type RendererLoader = () => Promise<CreateMatchRenderer>;

class LazyRenderer implements MatchRenderer {
  private inner: MatchRenderer | null = null;
  private destroyed = false;
  private options: Partial<RenderOptions> = {};
  private look: Readonly<ReplayLook> | null = null;

  constructor(
    private readonly load: RendererLoader,
    private readonly ctx: RendererContext,
  ) {}

  async mount(el: HTMLElement): Promise<void> {
    const create = await this.load();
    if (this.destroyed) return;
    const inner = create({ ...this.ctx, options: { ...this.ctx.options, ...this.options } });
    this.inner = inner;
    inner.setReplayLook?.(this.look);
    await inner.mount(el);
  }

  render(...args: Parameters<MatchRenderer['render']>): void {
    this.inner?.render(...args);
  }

  setOptions(opts: Partial<RenderOptions>): void {
    this.options = { ...this.options, ...opts };
    this.inner?.setOptions(opts);
  }

  setReplayLook(look: Readonly<ReplayLook> | null): void {
    this.look = look;
    this.inner?.setReplayLook?.(look);
  }

  cut(): void {
    this.inner?.cut?.();
  }

  worldToScreen(x: number, y: number, z: number, out: { x: number; y: number; s: number }): boolean {
    return this.inner?.worldToScreen?.(x, y, z, out) ?? false;
  }

  destroy(): void {
    this.destroyed = true;
    this.inner?.destroy();
    this.inner = null;
  }
}

/** A `CreateMatchRenderer` whose implementation is imported on first use (memoized). */
export const lazyRendererFactory = (load: RendererLoader): CreateMatchRenderer => {
  let pending: Promise<CreateMatchRenderer> | null = null;
  const once: RendererLoader = () => {
    pending ??= load().catch((e: unknown) => {
      pending = null; // a failed chunk load (offline blip) may be retried by the next match
      throw e;
    });
    return pending;
  };
  return (ctx) => new LazyRenderer(once, ctx);
};

/** Start loading a chunk when the browser is idle (never during first paint). */
export const whenIdle = (fn: () => void, timeoutMs = 2500): void => {
  const w = globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(fn, { timeout: timeoutMs });
  else setTimeout(fn, 600);
};
